import { faPlain } from 'utilities/helper/format'

/**
 * Where a member stands among the fund, as a filled bar.
 *
 * **Filled by percentile, not by the 0–100 score.** The score is the member's
 * Toman-days as a share of the fund's top scorer, and contribution in a real
 * fund is heavy-tailed: measured on صندوق خیریه برکت (579 members), rank 1
 * scored 100, rank 2 scored 31, and every one of the remaining 577 members
 * landed at 9 or below. The score was telling the truth — one member really
 * had provided three times what the next had — but as a *bar* it was blank
 * for 99% of the list, which is the one place a manager actually reads it.
 *
 * The percentile answers the question the row is for («کجای صندوق ایستاده؟»)
 * and cannot collapse, whatever the distribution. The magnitude — the raw
 * Toman-days and the ratio to the top — lives in the (i) breakdown, where
 * there is room to say what it means.
 */
export default function ScoreMeter({
    percentile,
    size = 'md',
}: {
    percentile: number
    size?: 'sm' | 'md'
}) {
    const clamped = Math.max(0, Math.min(100, percentile))
    const isEmpty = clamped === 0
    return (
        <div className=" flex items-center gap-2">
            <div
                className={` relative flex-1 overflow-hidden rounded-full bg-canvas ${
                    size === 'sm' ? ' h-1.5' : ' h-2'
                }`}
                role="img"
                aria-label={`بالاتر از ${faPlain.format(clamped)} درصد اعضا`}
            >
                <span
                    className={` absolute inset-y-0 right-0 rounded-full transition-[width] duration-500 motion-reduce:transition-none ${
                        isEmpty ? ' bg-stroke' : ' bg-brand'
                    }`}
                    style={{ width: `${Math.max(clamped, isEmpty ? 0 : 4)}%` }}
                />
            </div>
            <span
                className={` shrink-0 whitespace-nowrap font-bold tabular-nums ${
                    size === 'sm' ? ' text-[10px]' : ' text-[11px]'
                } ${isEmpty ? ' text-text-muted' : ' text-brand-deep'}`}
            >
                بالاتر از {faPlain.format(clamped)}٪
            </span>
        </div>
    )
}
